import styled from 'styled-components';
import {
  breakpoints,
  displayType,
  touchFeedback,
  textSize,
  brand,
  ink,
  surface,
  tones,
} from '../../../styles/brand';

export const PageWrapper = styled.div`
  ${touchFeedback}

  padding: 2rem;
  min-height: calc(100vh - var(--header-height));
  background: ${surface.page};

  @media (max-width: ${breakpoints.md}) {
    padding: 1rem;
  }
`;

export const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${surface.line};

  > a:first-child {
    background: none;
    display: flex;
    align-items: center;
    color: ${ink};
    text-align: center;
    margin-bottom: 1rem;
    font-family: 'RedhatDisplay-Bold';
    font-size: 1.1em;
    width: fit-content;
    transition: opacity 0.3s;
    opacity: 0.8;

    svg {
      margin-right: 0.4rem;
    }

    &:hover {
      opacity: 1;

      > svg {
        opacity: 1;
      }
    }
  }

  > div {
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }
`;

export const PageTitle = styled.h1`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(1.5)};
  ${displayType}
  color: ${ink};
  margin: 0;
`;

export const ContentLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: ${breakpoints.md}) {
    grid-template-columns: 1fr;
  }
`;

export const FormColumn = styled.div``;

export const ListColumn = styled.div`
  background: #fff;
  border: 1px solid ${surface.line};
  border-radius: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

export const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid ${surface.line};
  background: ${surface.page};

  h2 {
    font-family: 'RedHatDisplay-Bold';
    font-size: ${textSize(1)};
    color: ${ink};
    margin: 0;
  }
`;

export const EmptyState = styled.div`
  padding: 2.5rem 1.25rem;
  text-align: center;
  color: ${tones.gray.text};
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.9)};
`;

export const TeamCard = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid #f5f5f5;

  &:last-child {
    border-bottom: none;
  }
`;

export const TeamInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  flex: 1;
  min-width: 0;
`;

export const TeamName = styled.span`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.92)};
  color: ${ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TeamMeta = styled.span`
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.8)};
  color: ${tones.gray.text};
`;

export const MembersList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.3rem;
  margin-top: 0.25rem;
`;

export const MemberChip = styled.span`
  background: ${tones.blue.soft};
  color: ${brand.blue};
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.75)};
  padding: 0.15rem 0.5rem;
  border-radius: 0.35rem;
  border: 1px solid ${tones.blue.border};
`;

/* ── Confirm modal ── */
export const ConfirmOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
`;

export const ConfirmDialog = styled.div`
  background: #fff;
  border-radius: 0.75rem;
  padding: 1.75rem;
  width: min(92vw, 400px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.16);
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
`;

export const ConfirmTitle = styled.h3`
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(1.1)};
  color: ${ink};
  margin: 0;
`;

export const ConfirmText = styled.p`
  font-family: 'RedHatDisplay-Regular';
  font-size: ${textSize(0.9)};
  color: ${tones.gray.text};
  margin: 0;
  line-height: 1.5;

  strong {
    color: ${ink};
    font-family: 'RedHatDisplay-Bold';
  }
`;

export const ConfirmActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  margin-top: 0.25rem;
`;

export const DangerButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  height: 2.6rem;
  padding: 0 1.2rem;
  border: none;
  border-radius: 0.5rem;
  background: #dc2626;
  color: #fff;
  font-family: 'RedHatDisplay-Bold';
  font-size: ${textSize(0.9)};
  cursor: pointer;
  transition: background 0.18s;

  &:hover:not(:disabled) {
    background: #b91c1c;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

export const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
`;

export const Spinner = styled.div`
  width: 2rem;
  height: 2rem;
  border: 3px solid #f3f3f3;
  border-top: 3px solid ${brand.orange};
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
